import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import Icon from '../ui/Icon'
import DayPickCard from './DayPickCard'
import {
  displayShortDayOfWeek,
  displayShortMonth,
  isSameDate,
} from '../../utils/dateUtils'

type DayListProps = {
  days: Date[]
  /** Null when no day is picked (only with onClear). */
  selectedDay: Date | null
  setSelectedDay: (selectedDay: Date) => void
  /** Adds a card before the first day that opens a calendar for any day (past days only when allowed). */
  anyDay?: { disablePastDates?: boolean }
  /** Lets the picked day be unpicked: clicking it again (or the picked date in the calendar) calls this. */
  onClear?: () => void
}

/** Share of the visible width scrolled per arrow click, so the last visible day stays in view as context. */
const SCROLL_PAGE_RATIO = 0.8

const DayList = ({ days, selectedDay, setSelectedDay, anyDay, onClear }: DayListProps) => {
  const { t, i18n } = useTranslation('bookings')
  const listRef = useRef<HTMLDivElement>(null)
  const [canScrollLeft, setCanScrollLeft] = useState(false)
  const [canScrollRight, setCanScrollRight] = useState(false)

  const updateScrollState = useCallback(() => {
    const list = listRef.current
    if (!list) {
      return
    }
    setCanScrollLeft(list.scrollLeft > 0)
    setCanScrollRight(Math.ceil(list.scrollLeft + list.clientWidth) < list.scrollWidth)
  }, [])

  useEffect(() => {
    const list = listRef.current
    if (!list) {
      return
    }
    updateScrollState()
    const observer = new ResizeObserver(updateScrollState)
    observer.observe(list)
    return () => observer.disconnect()
  }, [updateScrollState, days.length])

  const scrollBy = (direction: -1 | 1) => {
    const list = listRef.current
    list?.scrollBy({ left: direction * list.clientWidth * SCROLL_PAGE_RATIO, behavior: 'smooth' })
  }

  const scrollable = canScrollLeft || canScrollRight
  const isSelected = (day: Date) => selectedDay !== null && isSameDate(day, selectedDay)
  const pick = (day: Date) => (onClear && isSelected(day) ? onClear() : setSelectedDay(day))

  return (
    <div className="day-list-wrap">
      {anyDay && (
        <DayPickCard
          pickedDay={selectedDay === null || days.some(isSelected) ? undefined : selectedDay}
          disablePastDates={anyDay.disablePastDates}
          onSelect={setSelectedDay}
          onClear={onClear}
        />
      )}
      {scrollable && (
        <button
          type="button"
          className="ph-iconbtn day-list-arrow"
          aria-label={t('booking.day-list.previous')}
          disabled={!canScrollLeft}
          onClick={() => scrollBy(-1)}
        >
          <Icon name="chevL" size={18} />
        </button>
      )}
      <div className="ph-row day-list" ref={listRef} onScroll={updateScrollState}>
        {days.map((day, index) => (
          <button
            key={index}
            className={`day-pill ${isSelected(day) ? 'on' : ''} ${index === 0 ? 'today' : ''}`}
            aria-pressed={isSelected(day)}
            title={onClear && isSelected(day) ? t('booking.day-list.clear-hint') : undefined}
            onClick={() => pick(day)}
          >
            <span className="day">
              {displayShortDayOfWeek(day, i18n.language)}
            </span>
            <span className="date">{day.getDate()}</span>
            <span className="indicator">
              {index === 0
                ? t('booking.booking-courts.today')
                : displayShortMonth(day, i18n.language).toLowerCase()}
            </span>
          </button>
        ))}
      </div>
      {scrollable && (
        <button
          type="button"
          className="ph-iconbtn day-list-arrow"
          aria-label={t('booking.day-list.next')}
          disabled={!canScrollRight}
          onClick={() => scrollBy(1)}
        >
          <Icon name="chevR" size={18} />
        </button>
      )}
    </div>
  )
}

export default DayList
